/**
 * Text helpers for the storefront product search: split the query into words, highlight matches,
 * and cut a description snippet around the first match.
 *
 * Matching mirrors ProductLuceneSearchCriteria: the query is split on spaces and each word is a
 * case-insensitive substring match.
 */
function searchWords(search) {
    const seen = new Set();

    return String(search ?? "")
        .split(" ")
        .map((word) => word.trim())
        .filter((word) => {
            const key = word.toLowerCase();
            if (word === "" || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
}

function escapeHtml(text) {
    return String(text ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wordsPattern(words) {
    if (!words.length) return null;

    // longest first so "keyboard" wins over "key" at the same position
    const alternatives = [...words].sort((a, b) => b.length - a.length).map(escapeRegExp);

    return new RegExp(alternatives.join("|"), "gi");
}

// Escaped HTML with every word wrapped in <mark>; safe for v-html
function highlightMatches(text, words) {
    const source = String(text ?? "");
    const pattern = wordsPattern(words);
    if (!pattern || source === "") return escapeHtml(source);

    let html = "";
    let last = 0;
    let match;
    while ((match = pattern.exec(source)) !== null) {
        if (match[0] === "") {
            pattern.lastIndex++;
            continue;
        }
        html += escapeHtml(source.slice(last, match.index));
        html += `<mark class="search-highlight">${escapeHtml(match[0])}</mark>`;
        last = match.index + match[0].length;
    }

    return html + escapeHtml(source.slice(last));
}

// tags that start a new line when rendered
const LINE_BREAK_TAGS = /<(?:br|\/?(?:p|div|li|ul|ol|tr|td|th|h[1-6]|table|blockquote|section|article))\b[^>]*>/gi;

// text with one "\n" per rendered line break
function plainLines(html) {
    if (!html) return "";

    const marked = String(html).replace(LINE_BREAK_TAGS, "\n$&");
    const text =
        typeof DOMParser !== "undefined"
            ? new DOMParser().parseFromString(marked, "text/html").body.textContent
            : marked.replace(/<[^>]*>/g, "");

    return text
        .replace(/[^\S\n]+/g, " ")
        .replace(/ ?\n\s*/g, "\n")
        .trim();
}

function plainText(html) {
    return plainLines(html).replace(/\n/g, " ");
}

function firstMatchIndex(text, words) {
    const lower = text.toLowerCase();

    return words.reduce((first, word) => {
        const index = lower.indexOf(word.toLowerCase());
        return index !== -1 && (first === -1 || index < first) ? index : first;
    }, -1);
}

/**
 * Plain-text snippet around the first matched word: starts at that sentence when the match is within
 * `lead` characters of its start, otherwise about `lead` characters before the match, so the match
 * stays in view on a narrow 2-line card. Cut at whole words, with "…" where text was dropped.
 * Empty when nothing matches.
 */
function descriptionSnippet(html, words, maxLength = 160, lead = 20) {
    const lines = plainLines(html);
    const index = firstMatchIndex(lines, words);
    if (index === -1) return "";

    // a line break ends a sentence too
    const sentenceStart = Math.max(...[".", "!", "?", ";", "\n"].map((mark) => lines.lastIndexOf(mark, index - 1))) + 1;
    const text = lines.replace(/\n/g, " ");
    // keep the sentence start when the match is near it, otherwise begin a few words before the match
    let start = index - sentenceStart <= lead ? sentenceStart : index - lead;
    if (start > sentenceStart) {
        // move forward to the next word start so no word is cut
        const space = text.indexOf(" ", start);
        start = space !== -1 && space < index ? space + 1 : index;
    }
    while (text[start] === " ") start++;

    let end = start + maxLength;
    if (end < text.length) {
        const space = text.lastIndexOf(" ", end);
        end = space > index ? space : end;
    } else {
        end = text.length;
    }

    return (start > 0 ? "…" : "") + text.slice(start, end).trim() + (end < text.length ? "…" : "");
}

module.exports = { searchWords, escapeHtml, highlightMatches, plainText, descriptionSnippet };
